import React from 'react'
import {useTranslation} from 'react-i18next'
import {Col, Layout, Result, Row, Typography} from 'antd'
import {observer} from 'mobx-react'
import {useInjection} from 'dna-react-ioc'
import classNames from 'classnames'
import {Link} from 'react-router-dom'
import {ROUTES} from '@/router/routes'
import {withTitle} from '@/router/utils'
import {useScreenType, useWindowSize} from '@/hooks'
import {PageHeader} from '@/components/dumb/PageHeader'
import {
    RangePickerContainer,
    SelectWithTitleContainer,
    TabsContainer,
    TTabsContainerStore
} from '@/components/containers'
import {
    analyticsConfigMapOriginal,
    currencySelectStoreMapOriginal,
    OverviewEcomStoreSelectStoreSymbol,
    OverviewPosStoreSelectStoreSymbol,
    OverviewTabsContainerStoreSymbol,
    rangePickerConfigMapOriginal
} from '~/code/pages/Overview/constants'
import {hasPermissions} from '@/stores/auth/services'
import {PermissionsMap} from '@/stores/auth/constants/permissions-map'
import {isOctoberRestrictionEnabled} from '@/constants/dateRangePresets'
import {disabledDatesBeforeOctober2025} from '@/utils/moment-utils'
import { StoreSelectContainer } from '~/code/components/containers/transaction'
import { TerminalType } from '~/code/constants'
import styles from './styles.scss'


const Overview: React.FC = observer(() => {
    const {t} = useTranslation()
    const tabsStore = useInjection<TTabsContainerStore>(OverviewTabsContainerStoreSymbol)
    const [windowWidth] = useWindowSize()
    const {isMobile, isDesktop} = useScreenType()
    const rangePickerConfig = rangePickerConfigMapOriginal[tabsStore.value]
    const currencySelectInjectableIdentifier = currencySelectStoreMapOriginal[tabsStore.value]
    const currentAnalyticsConfig = analyticsConfigMapOriginal[tabsStore.value]

    const disabledDate = (current) => {
        if (!current) return false

        return isOctoberRestrictionEnabled && disabledDatesBeforeOctober2025(current)
    }

    return (
        <>
            <PageHeader
                className={styles.header}
                title={
                    <Row gutter={isMobile ? [16, 16] : [32, 32]}>
                        <Col>
                            <Typography.Title className={styles.title} level={4}>
                                {t('Overview.title')}
                            </Typography.Title>
                        </Col>
                        {hasPermissions([PermissionsMap.reports.read]) && (
                            <Col className={styles.detailedReport}>
                                <Link className={styles.link} to={ROUTES.reports}>
                                    {t('Overview.detailedReport')}
                                </Link>
                            </Col>
                        )}
                    </Row>
                }
                border={'none'}
            >
                {!isDesktop &&
                hasPermissions([
                    PermissionsMap.online_payments.read,
                    PermissionsMap.online_payments.full,
                    PermissionsMap.pos_payments.read,
                    PermissionsMap.pos_payments.full
                ]) ? (
                    <div
                        className={classNames(styles.tabBarExtra, {
                            [styles.tabBarExtraMobile]: isMobile
                        })}
                    >
                        <StoreSelectContainer
                                className={classNames(styles.store, {
                                    [styles.storeMobile]: isMobile
                                })}
                                type={
                                    tabsStore.value === 'onlinePayments'
                                        ? TerminalType.ECOM
                                        : TerminalType.POS
                                }
                                ecomInjectableIdentifier={OverviewEcomStoreSelectStoreSymbol}
                                posInjectableIdentifier={OverviewPosStoreSelectStoreSymbol}
                        />
                        <SelectWithTitleContainer
                            className={classNames(styles.currency, {
                                [styles.currencyMobile]: isMobile
                            })}
                            injectableIdentifier={currencySelectInjectableIdentifier}
                            displayValue={t('Overview.currency')}
                        />
                        <RangePickerContainer
                            key={rangePickerConfig.key}
                            showRangePickerTime={true}
                            disabledDate={disabledDate}
                            injectableIdentifier={rangePickerConfig.injectableIdentifier}
                        />
                    </div>
                ) : null}
                {hasPermissions([
                    PermissionsMap.online_payments.read,
                    PermissionsMap.online_payments.full,
                    PermissionsMap.pos_payments.read,
                    PermissionsMap.pos_payments.full
                ]) ? (
                    <TabsContainer
                        injectableIdentifier={OverviewTabsContainerStoreSymbol}
                        className={classNames(
                            {
                                [styles.overviewTabs]: !hasPermissions([
                                    PermissionsMap.settlements.read,
                                    PermissionsMap.settlements.full
                                ])
                                    ? windowWidth < 1480
                                    : windowWidth < 1550
                            },
                            styles.tabs
                        )}
                        tabBarExtraContent={
                            isDesktop ? (
                                <div
                                    className={classNames(styles.tabBarExtra, {
                                        [styles.tabBarExtraMobile]: isMobile
                                    })}
                                >
                                    <StoreSelectContainer
                                        className={classNames(styles.store, {
                                            [styles.storeMobile]: isMobile
                                        })}
                                        type={
                                            tabsStore.value === 'onlinePayments'
                                                ? TerminalType.ECOM
                                                : TerminalType.POS
                                        }
                                        ecomInjectableIdentifier={OverviewEcomStoreSelectStoreSymbol}
                                        posInjectableIdentifier={OverviewPosStoreSelectStoreSymbol}
                                    />
                                    <SelectWithTitleContainer
                                        className={classNames(styles.currency, {
                                            [styles.currencyMobile]: isMobile
                                        })}
                                        injectableIdentifier={currencySelectInjectableIdentifier}
                                        displayValue={t('Overview.currency')}
                                    />
                                    <RangePickerContainer
                                        key={rangePickerConfig.key}
                                        className={styles.picker}
                                        showRangePickerTime={
                                            true
                                        }
                                        disabledDate={disabledDate}
                                        injectableIdentifier={
                                            rangePickerConfig.injectableIdentifier
                                        }
                                    />
                                </div>
                            ) : null
                        }
                    />
                ) : (
                    <Result status='404' title={t('Overview.noContentTitle')} />
                )}
            </PageHeader>

            <Layout.Content className={styles.body}>
                {currentAnalyticsConfig &&
                    hasPermissions(currentAnalyticsConfig.permissions) &&
                    currentAnalyticsConfig.component}
            </Layout.Content>
        </>
    )
})

export default withTitle(Overview)
